import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import categoryService from '../../services/categoryService';
import ballotService from '../../services/ballotService';
import userService from '../../services/userService';
import UserInfoModal from './UserInfoModal';
import { getStorageItem, setStorageItem } from '../../utils/localStorage';
import { getCategoryWinners, getCategoryWinnerIds } from '../../utils/categoryWinners';
import { useBonusQuestions } from '../../contexts/BonusQuestionsContext';
import { useBeta } from '../../contexts/BetaContext';
import { useSettings } from '../../contexts/SettingsContext';
import './BallotSubmissionPage.css';

const isMemoriamQuestion = (questionText = '') => (
  questionText.toLowerCase().includes('memoriam')
);

const BallotSubmissionPage = ({ homePathOverride = null }) => {
  const navigate = useNavigate();
  const { bonusQuestions } = useBonusQuestions();
  const { activePartyId, basePath } = useBeta();
  const { settings, isSubmissionLocked, timeUntilDeadline, formattedDeadline } = useSettings();
  const submissionLocked = isSubmissionLocked;
  const homePath = homePathOverride || basePath || '/';
  const hasPartyEnded = Boolean(settings?.dateEnded);
  const submissionDeadlineValue = settings?.ballotSubmissionDeadline;
  const submissionDeadlineDate = submissionDeadlineValue ? new Date(submissionDeadlineValue) : null;
  const hideSubmissionDeadlineUi = submissionDeadlineDate && !Number.isNaN(submissionDeadlineDate.getTime())
    ? Date.now() >= submissionDeadlineDate.getTime() + (30 * 60 * 1000)
    : false;

  // Get partyId from context
  const partyId = activePartyId;

  // Get stored values with party-specific keys
  const storedBallotId = activePartyId ? getStorageItem('ballotId', activePartyId) : null;
  const storedUserId = activePartyId ? getStorageItem('userId', activePartyId) : null;

  // User state
  const [userId, setUserId] = useState(storedUserId || null);
  const [showUserModal, setShowUserModal] = useState(!storedUserId);
  const [userModalLoading, setUserModalLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [ballot, setBallot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [ballotDocumentId, setBallotDocumentId] = useState(null);

  // Draft saving
  const draftSaveTimerRef = useRef(null);

  // Update user state when activePartyId changes (switching between v1/v2)
  useEffect(() => {
    if (activePartyId) {
      const newUserId = getStorageItem('userId', activePartyId);
      setUserId(newUserId || null);
      setShowUserModal(!newUserId);
    }

    // Reset ballot state when party changes
    setBallot(null);
    setBallotDocumentId(null);
    setIsUpdateMode(false);
  }, [activePartyId]);

  // Merge categories with ballot selections (categories are already sorted by order from fetch)
  const categoriesWithSelections = useMemo(() => {
    if (!categories.length) return [];

    // Create a Map for O(1) lookup of selections from ballot
    const selectionsMap = new Map();
    if (ballot?.userCategoriesSelections) {
      ballot.userCategoriesSelections.forEach(sel => {
        selectionsMap.set(sel.categoryId, {
          nomineeId: sel.selection?.nomineeId || null,
          isCorrect: typeof sel.isCorrect === 'boolean' ? sel.isCorrect : null
        });
      });
    }

    // Merge categories with user's selections
    return categories.map(category => ({
      ...category,
      selectedNomineeId: selectionsMap.get(category.id)?.nomineeId || null
    }));
  }, [categories, ballot]);

  const [formData, setFormData] = useState({
    predictions: {}
  });
  const [bonusQuestionResponses, setBonusQuestionResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'

  // Handle user info submission
  const handleUserInfoSubmit = async (userInfo) => {
    setUserModalLoading(true);
    try {
      let newUserId;

      // If existing user was found, use that userId
      if (userInfo.existingUser) {
        newUserId = userInfo.existingUser.id;
        // Update name if it changed
        if (userInfo.name.trim() !== userInfo.existingUser.name) {
          await userService.updateUser(newUserId, { name: userInfo.name.trim() });
        }
      } else {
        // Find or create user
        const result = await userService.findOrCreateUser(
          userInfo.email,
          userInfo.name
        );
        newUserId = result.userId;
        // isNew available if needed: result.isNew
      }

      // Store user info in localStorage with party-specific keys
      setStorageItem('userId', newUserId, partyId);
      setStorageItem('userEmail', userInfo.email, partyId);
      setStorageItem('userName', userInfo.name, partyId);

      setUserId(newUserId);
      setShowUserModal(false);

      // Check if user has existing ballot for this party (using userId as document ID)
      if (!storedBallotId) {
        try {
          const existingBallot = await ballotService.fetchBallotById(partyId, newUserId);
          if (existingBallot) {
            // Found existing ballot, load it
            setBallot(existingBallot);
            setIsUpdateMode(true);
            setBallotDocumentId(existingBallot.id); // This is now userId
            // Store ballotId (which is userId) in localStorage for future reference
            setStorageItem('ballotId', existingBallot.ballotId || newUserId, partyId);
          }
        } catch (ballotError) {
          console.error('Error checking for existing ballot:', ballotError);
          // Continue without existing ballot (will create new one)
        }
      }
    } catch (err) {
      console.error('Error setting up user:', err);
      setError(err.message || 'Failed to set up user account');
    } finally {
      setUserModalLoading(false);
    }
  };

  // Fetch categories and ballot on mount (only after user is set up)
  useEffect(() => {
    if (!userId && !showUserModal) {
      // Wait for user info
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch categories from Firestore
        const firestoreCategories = await categoryService.fetchCategories(partyId);
        const fetchedCategories = firestoreCategories.map(category => {
          const winners = getCategoryWinners(category);
          const winnerIds = getCategoryWinnerIds(category);
          return {
            id: category.categoryId || category.id,
            name: category.name,
            points: category.points,
            order: category.order,
            date_created: category.dateCreated,
            date_updated: category.dateUpdated,
            winner: winners.length > 0 ? {
              id: winners[0].winnerId,
              code: winners[0].winnerCode,
              text: winners[0].winnerText
            } : undefined,
            winners,
            nominees: (category.nominees || []).map(nominee => ({
              id: nominee.nomineeId || nominee.id,
              name: nominee.nomineeText || nominee.name,
              code: nominee.nomineeCode || nominee.code,
              isWinner: winnerIds.has(nominee.nomineeId || nominee.id)
            }))
          };
        });

        // Sort categories by order if available
        fetchedCategories.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return (a.name || '').localeCompare(b.name || '');
        });

        setCategories(fetchedCategories);

        // 2. Fetch user's ballot if userId exists (ballotId is now userId)
        if (userId) {
          try {
            // Use userId as document ID to fetch ballot
            const userBallot = await ballotService.fetchBallotById(partyId, userId);
            if (userBallot) {
              setBallot(userBallot);
              setIsUpdateMode(true);
              setBallotDocumentId(userBallot.id); // This is userId
            }
          } catch (ballotError) {
            console.error('Error fetching ballot:', ballotError);
            // Continue without ballot (create mode)
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
      }
    };

    fetchData();
    // Note: ballotId removed from deps - we use userId to fetch ballot (ballotId === userId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, userId, showUserModal]);

  // Initialize form data with categories and existing ballot selections
  useEffect(() => {
    if (categoriesWithSelections.length > 0) {
      const initialPredictions = {};
      categoriesWithSelections.forEach(category => {
        // Use existing selection from ballot if available, otherwise empty
        initialPredictions[category.id] = category.selectedNomineeId || '';
      });
      setFormData({
        predictions: initialPredictions
      });
    }
  }, [categoriesWithSelections]);

  // Initialize bonus question responses from ballot
  useEffect(() => {
    if (ballot?.userBonusQuestionsResponses && bonusQuestions.length > 0) {
      const initialResponses = {};
      ballot.userBonusQuestionsResponses.forEach(response => {
        initialResponses[response.bonusQuestionId] = response.response || '';
      });
      setBonusQuestionResponses(initialResponses);
    } else if (bonusQuestions.length > 0 && !ballot?.userBonusQuestionsResponses) {
      // Initialize empty responses for all bonus questions
      const initialResponses = {};
      bonusQuestions.forEach(question => {
        initialResponses[question.id] = '';
      });
      setBonusQuestionResponses(initialResponses);
    }
  }, [ballot, bonusQuestions]);

  const handlePredictionChange = (categoryId, nomineeId) => {
    setSubmitError(null);
    setMissingCategoryIds(prev => prev.filter(id => id !== categoryId));
    setFormData(prev => ({
      ...prev,
      predictions: {
        ...prev.predictions,
        [categoryId]: nomineeId
      }
    }));
  };

  const handleBonusQuestionChange = (bonusQuestionId, response) => {
    setBonusQuestionResponses(prev => ({
      ...prev,
      [bonusQuestionId]: response
    }));
  };

  // Debounced draft saving to Firestore (only saves if ballot is still a draft)
  useEffect(() => {
    // Don't auto-save if submissions are locked
    if (submissionLocked) {
      return;
    }

    // Don't auto-save if ballot has been submitted (isDraft === false)
    if (ballot?.isDraft === false) {
      return;
    }

    if (!userId || !categoriesWithSelections.length || Object.keys(formData.predictions).length === 0) {
      return;
    }

    // Clear existing timer
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }

    // Check if there are actual selections (not all empty)
    const hasSelections = Object.values(formData.predictions).some(val => val);
    if (!hasSelections) {
      return;
    }

    // Set up debounced save (2 seconds after last change)
    draftSaveTimerRef.current = setTimeout(async () => {
      setDraftSaveStatus('saving');

      try {
        const userCategoriesSelections = Object.entries(formData.predictions)
          .filter(([_, nomineeId]) => nomineeId)
          .map(([categoryId, nomineeId]) => {
            // Find the category and nominee to get full details
            const category = categoriesWithSelections.find(cat => cat.id === categoryId);
            const nominee = category?.nominees?.find(nom => nom.id === nomineeId);

            return {
              categoryId,
              selection: {
                nomineeId: nomineeId,
                nomineeCode: nominee?.code || '',
                nomineeText: nominee?.name || ''
              }
            };
          });

        // Format bonus question responses
        const userBonusQuestionsResponses = Object.entries(bonusQuestionResponses)
          .reduce((acc, [bonusQuestionId, response]) => {
            const question = bonusQuestions.find((item) => (
              (item.id || item.bonusQuestionId) === bonusQuestionId
            ));
            const trimmedResponse = (response || '').trim();

            if (trimmedResponse) {
              acc.push({
                bonusQuestionId,
                response: trimmedResponse
              });
              return acc;
            }

            if (isMemoriamQuestion(question?.question || '')) {
              acc.push({
                bonusQuestionId,
                response: '0'
              });
            }

            return acc;
          }, []);

        // Check if all categories have been answered - if so, mark as submitted (not draft)
        const allCategoriesAnswered = categoriesWithSelections.length > 0 &&
          categoriesWithSelections.every(cat => formData.predictions[cat.id]);

        const updateData = {
          userCategoriesSelections,
          isDraft: !allCategoriesAnswered
        };

        if (userBonusQuestionsResponses.length > 0) {
          updateData.userBonusQuestionsResponses = userBonusQuestionsResponses;
        }

        if (isUpdateMode && ballotDocumentId) {
          // Update existing draft (ballotDocumentId is userId)
          await ballotService.updateBallot(partyId, ballotDocumentId, updateData);
        } else if (userId) {
          // Create or update draft ballot using userId as document ID
          try {
            // Try to fetch existing ballot using userId as document ID
            const existingBallot = await ballotService.fetchBallotById(partyId, userId);
            if (existingBallot) {
              await ballotService.updateBallot(partyId, userId, updateData);
              if (!ballotDocumentId) {
                setBallotDocumentId(userId);
                setIsUpdateMode(true);
              }
            } else {
              // Create new ballot (document ID will be userId)
              await ballotService.createBallot(partyId, {
                oscarsPartyId: partyId,
                userId,
                userCategoriesSelections,
                userBonusQuestionsResponses: userBonusQuestionsResponses.length > 0 ? userBonusQuestionsResponses : undefined,
                isDraft: !allCategoriesAnswered
              });
              // Store userId as ballotId in localStorage
              setStorageItem('ballotId', userId, partyId);
              setBallotDocumentId(userId);
              setIsUpdateMode(true);
            }
          } catch (err) {
            // If fetch fails, create new ballot
            await ballotService.createBallot(partyId, {
              oscarsPartyId: partyId,
              userId,
              userCategoriesSelections,
              userBonusQuestionsResponses: userBonusQuestionsResponses.length > 0 ? userBonusQuestionsResponses : undefined,
              isDraft: !allCategoriesAnswered
            });
            // Store userId as ballotId in localStorage
            setStorageItem('ballotId', userId, partyId);
            setBallotDocumentId(userId);
            setIsUpdateMode(true);
          }
        }

        // Show "Ballot saved!" then fade out after 3.5 seconds
        setDraftSaveStatus('saved');
        setTimeout(() => setDraftSaveStatus('idle'), 3500);
      } catch (err) {
        console.error('Error saving draft:', err);
        // Reset status on error (don't show error to user for auto-save failures)
        setDraftSaveStatus('idle');
      }
    }, 2000); // 2 second debounce

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [formData.predictions, bonusQuestionResponses, userId, partyId, categoriesWithSelections, isUpdateMode, ballotDocumentId, submissionLocked, ballot, bonusQuestions]);

  const scrollTargetRef = useRef(null);
  const [missingCategoryIds, setMissingCategoryIds] = useState([]);

  // Auto-dismiss error banner after 3.5s (same cadence as "Ballot saved!")
  useEffect(() => {
    if (!submitError) return;
    const timer = setTimeout(() => setSubmitError(null), 3500);
    return () => clearTimeout(timer);
  }, [submitError]);

  const handlePrintMyBallot = () => {
    const userName = partyId ? getStorageItem('userName', partyId) || '—' : '—';
    const partyName = settings?.partyName || 'MR Oscar Party';
    const ballotYear = formattedDeadline ? new Date(formattedDeadline).getFullYear() : new Date().getFullYear();

    const categoryBlock = (cat) => {
      const selectedNomineeId = cat.selectedNomineeId || formData.predictions[cat.id];
      const nominees = cat.nominees || [];
      const pts = cat.points ? ` (${cat.points} pts)` : '';
      const nomineeLines = nominees.map((n, idx) => {
        const isSelected = n.id === selectedNomineeId;
        const checkbox = isSelected ? '☑' : '☐';
        const letter = n.code || String.fromCharCode(65 + idx);
        const name = n.name || '—';
        return `<div class="nominee ${isSelected ? 'selected' : ''}">${checkbox} ${letter}. ${name}</div>`;
      }).join('');
      return `<div class="category">
        <div class="category-header">${cat.order}. ${cat.name}${pts}</div>
        <div class="nominees">${nomineeLines}</div>
      </div>`;
    };

    const cats = categoriesWithSelections;
    const third = Math.ceil(cats.length / 3);
    const col1 = cats.slice(0, third).map(categoryBlock).join('');
    const col2 = cats.slice(third, third * 2).map(categoryBlock).join('');
    const col3 = cats.slice(third * 2).map(categoryBlock).join('');

    const bonusBlock = bonusQuestions.length > 0 ? `
      <div class="bonus-section">
        <div class="bonus-header">Bonus Question:</div>
        ${bonusQuestions.map(q => {
      const resp = bonusQuestionResponses[q.id] || '—';
      return `<div class="bonus-item">
            <div class="bonus-question">${q.question}</div>
            <div class="bonus-answer">${resp}</div>
          </div>`;
    }).join('')}
      </div>` : '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>My Ballot - ${partyName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: letter; margin: 0.4in; }
    html, body { height: 100%; }
    body { display: flex; flex-direction: column; font-family: Georgia, serif; font-size: 8px; padding: 0.4in; line-height: 1.15; color: #111; }
    .ballot-header { flex-shrink: 0; margin-bottom: 6px; }
    .ballot-header-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 2px; }
    .ballot-name { font-size: 10px; }
    .ballot-tagline { font-size: 9px; font-weight: 600; }
    .ballot-title { font-size: 11px; font-weight: 700; }
    .ballot-columns { flex: 1; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; min-height: 0; align-content: stretch; }
    .ballot-col { min-width: 0; min-height: 100%; }
    .category { margin-bottom: 5px; }
    .category-header { font-weight: 700; margin-bottom: 1px; }
    .nominees { margin-left: 10px; }
    .nominee { margin-bottom: 0; }
    .nominee.selected { font-weight: 700; }
    .bonus-section { margin-top: 6px; margin-bottom: 4px; }
    .bonus-header { font-weight: 700; margin-bottom: 2px; }
    .bonus-question { margin-bottom: 1px; }
    .bonus-answer { font-weight: 600; margin-left: 10px; }
    @media print { body { min-height: 100vh; } .ballot-columns { min-height: 9in; } }
  </style>
</head>
<body>
  <div class="ballot-header">
    <div class="ballot-header-row">
      <span class="ballot-name">Name: ${userName}</span>
      <span class="ballot-tagline">You Must be Present to Win!!</span>
    </div>
    <div class="ballot-title">The Official ${partyName} Ballot ${ballotYear}</div>
  </div>
  <div class="ballot-columns">
    <div class="ballot-col">${col1}</div>
    <div class="ballot-col">${col2}</div>
    <div class="ballot-col">${col3}${bonusBlock}</div>
  </div>
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;left:-9999px;top:0;width:0;height:0;border:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.contentWindow.onafterprint = () => document.body.removeChild(iframe);
    setTimeout(() => iframe.contentWindow.print(), 100);
  };

  const validateForm = () => {
    // Check if all categories have predictions
    const missingPredictions = categoriesWithSelections.filter(
      category => !formData.predictions[category.id]
    );
    if (missingPredictions.length > 0) {
      const missingIds = missingPredictions.map(c => c.id);
      setMissingCategoryIds(missingIds);
      setSubmitError(`Please complete your ballot — you're missing ${missingPredictions.length} of ${categoriesWithSelections.length} category predictions.`);
      // Scroll to first missing category; if first is missing, scroll to top
      setTimeout(() => {
        const el = scrollTargetRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const targetScrollY = window.scrollY + rect.top - 100; // 100px from top of viewport
          window.scrollTo({ top: Math.max(0, targetScrollY), behavior: 'smooth' });
        }
      }, 150);
      return false;
    }

    setMissingCategoryIds([]);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setMissingCategoryIds([]);

    // Check if submissions are locked
    if (submissionLocked) {
      setSubmitError('Submissions are closed. The deadline has passed.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Format user selections to match Firestore structure
      const userCategoriesSelections = Object.entries(formData.predictions)
        .filter(([_, nomineeId]) => nomineeId) // Only include selections with a value
        .map(([categoryId, nomineeId]) => {
          // Find the category and nominee to get full details
          const category = categoriesWithSelections.find(cat => cat.id === categoryId);
          const nominee = category?.nominees?.find(nom => nom.id === nomineeId);

          return {
            categoryId,
            selection: {
              nomineeId: nomineeId,
              nomineeCode: nominee?.code || '',
              nomineeText: nominee?.name || ''
            }
          };
        });

      // Format bonus question responses
      const userBonusQuestionsResponses = Object.entries(bonusQuestionResponses)
        .reduce((acc, [bonusQuestionId, response]) => {
          const question = bonusQuestions.find((item) => (
            (item.id || item.bonusQuestionId) === bonusQuestionId
          ));
          const trimmedResponse = (response || '').trim();

          if (trimmedResponse) {
            acc.push({
              bonusQuestionId,
              response: trimmedResponse
            });
            return acc;
          }

          if (isMemoriamQuestion(question?.question || '')) {
            acc.push({
              bonusQuestionId,
              response: '0'
            });
          }

          return acc;
        }, []);

      const submitData = {
        userCategoriesSelections,
        isDraft: false
      };

      if (userBonusQuestionsResponses.length > 0) {
        submitData.userBonusQuestionsResponses = userBonusQuestionsResponses;
      }

      if (isUpdateMode && ballotDocumentId) {
        // Update existing ballot (mark as submitted, not draft)
        // ballotDocumentId is userId
        await ballotService.updateBallot(partyId, ballotDocumentId, submitData);
      } else {
        // Create new ballot (document ID will be userId)
        const documentId = await ballotService.createBallot(partyId, {
          oscarsPartyId: partyId,
          userId,
          userCategoriesSelections,
          userBonusQuestionsResponses: userBonusQuestionsResponses.length > 0 ? userBonusQuestionsResponses : undefined,
          isDraft: false
        });

        // Store userId as ballotId in localStorage for future reference
        setStorageItem('ballotId', documentId, partyId); // documentId is userId
        setBallotDocumentId(documentId);
        setIsUpdateMode(true);
      }

      // Update ballot state with the submitted data (no longer a draft)
      setBallot(prev => ({
        ...prev,
        userCategoriesSelections,
        userBonusQuestionsResponses: userBonusQuestionsResponses.length > 0 ? userBonusQuestionsResponses : prev?.userBonusQuestionsResponses,
        isDraft: false
      }));

      // Show submission confirmation overlay
      setShowSubmissionConfirmation(true);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit ballot. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ballot-submission-page">
      {showUserModal && (
        <UserInfoModal
          onSubmit={handleUserInfoSubmit}
          onCancel={null}
          isLoading={userModalLoading}
          closePathOverride={homePath}
          forceReturningUser={hasPartyEnded}
        />
      )}
      {/* Bottom-leading banner: error (missing selections) or draft save status */}
      {(submitError || draftSaveStatus !== 'idle') && (
        <div
          className={`draft-save-indicator ${submitError ? 'draft-save-indicator--error' : draftSaveStatus}`}
          role={submitError ? 'alert' : undefined}
        >
          {submitError
            ? submitError
            : draftSaveStatus === 'saving'
              ? 'Saving...'
              : 'Ballot saved! You can safely return at any time to finish your ballot before the deadline.'}
        </div>
      )}
      {showSubmissionConfirmation && (
        <div className="submission-confirmation-overlay">
          <div className="submission-confirmation-modal">
            <div className="confirmation-icon">✓</div>
            <h2 className="confirmation-title">Ballot Submitted!</h2>
            <div className="confirmation-notices">
              <p className="notice-important">You must be present to win!</p>
              <div className="notice-payment-card">
                <p className="notice-payment">Please bring $10 cash to the party for official submission</p>
              </div>
            </div>
            <div className="confirmation-actions">
              <button
                type="button"
                className="confirmation-actions__btn confirmation-actions__btn--secondary"
                onClick={() => setShowSubmissionConfirmation(false)}
              >
                View My Ballot
              </button>
              <button
                type="button"
                className="confirmation-actions__btn confirmation-actions__btn--secondary"
                onClick={handlePrintMyBallot}
              >
                Print My Ballot
              </button>
              <div className="confirmation-actions__btn-wrap">
                <button
                  type="button"
                  className="confirmation-actions__btn confirmation-actions__btn--primary"
                  onClick={() => navigate(homePath)}
                >
                  Return Home
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="submission-container">
        {loading ? (
          <div className="ballot-loading">
            <div className="loading-spinner"></div>
            <p>Loading ballot...</p>
          </div>
        ) : error ? (
          <div className="empty-state">
            <p>Error loading data: {error}</p>
          </div>
        ) : categoriesWithSelections.length === 0 ? (
          <div className="empty-state">
            <p>No categories available yet. Please check back later.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="ballot-form">
            {/* Header Section - Title and Reminder (scroll target when first category is missing) */}
            <div
              ref={missingCategoryIds.length > 0 && categoriesWithSelections.findIndex(c => c.id === missingCategoryIds[0]) <= 0 ? scrollTargetRef : null}
              className="ballot-header"
            >
              <h1 className="ballot-title">
                The Official {settings?.partyName || 'MR Oscar Party'} Ballot
              </h1>

              {/* Submission Lock Banner */}
              {!hideSubmissionDeadlineUi && submissionLocked && (
                <div className="submission-locked-banner">
                  <span className="lock-icon">🔒</span>
                  <span className="lock-message">
                    Submissions are closed. The deadline passed on {formattedDeadline}.
                  </span>
                </div>
              )}

              {/* Deadline Countdown - Urgent */}
              {!hideSubmissionDeadlineUi && !submissionLocked && formattedDeadline && timeUntilDeadline && timeUntilDeadline.totalMs > 0 && timeUntilDeadline.totalMs < 24 * 60 * 60 * 1000 && (
                <span className="countdown-urgent">
                  ⏰ Only{' '}
                  {timeUntilDeadline.hours > 0 && `${timeUntilDeadline.hours}h `}
                  {timeUntilDeadline.minutes}m {timeUntilDeadline.seconds}s left to submit!
                </span>
              )}

              {/* Deadline Reminder - Non-urgent */}
              {!hideSubmissionDeadlineUi && !submissionLocked && formattedDeadline && !(timeUntilDeadline && timeUntilDeadline.totalMs > 0 && timeUntilDeadline.totalMs < 24 * 60 * 60 * 1000) && (
                <span className="ballot-reminder-inline">
                  <span className="reminder-label">Reminder:</span>{' '}
                  Ballots are due by <strong>{formattedDeadline}</strong>
                </span>
              )}

              {/* Fallback to party date if no submission deadline */}
              {!hideSubmissionDeadlineUi && !formattedDeadline && settings?.partyDate && (
                <span className="ballot-reminder-inline">
                  <span className="reminder-label">Reminder:</span>{' '}
                  Ballots are due by{' '}
                  <strong>
                    {new Date(settings.partyDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                      timeZone: 'America/Los_Angeles',
                      timeZoneName: 'short'
                    })}
                  </strong>
                </span>
              )}
            </div>

            {/* User name beneath ballot title - name prominent, label subtle */}
            <div className="ballot-user-section">
              <span className="ballot-user-label">Name</span>
              <span className="ballot-user-name">
                {partyId ? getStorageItem('userName', partyId) || '—' : '—'}
              </span>
            </div>

            {/* Three-Column Layout for Categories and Bonus Questions */}
            <div className="ballot-categories-container">
              {categoriesWithSelections.map((category, index) => {
                const nominees = category.nominees || [];
                const winnerIds = getCategoryWinnerIds(category);
                const hasWinner = winnerIds.size > 0;
                const isMissing = missingCategoryIds.includes(category.id);
                const firstMissingIndex = missingCategoryIds.length > 0
                  ? categoriesWithSelections.findIndex(c => c.id === missingCategoryIds[0])
                  : -1;
                const isScrollTarget = index === firstMissingIndex;

                return (
                  <div
                    key={category.id}
                    ref={isScrollTarget ? scrollTargetRef : null}
                    className={`ballot-category ${isMissing ? 'ballot-category--missing' : ''}`}
                  >
                    <div className="category-header">
                      <strong className="category-number">{category.order}.</strong>
                      <strong className="category-name">
                        {category.name}
                        {category.points && (
                          <span className="category-points"> ({category.points} pts)</span>
                        )}
                      </strong>
                    </div>
                    <div className="category-nominees">
                      {nominees.length > 0 ? (
                        nominees.map((nominee) => {
                          const isSelected = formData.predictions[category.id] === nominee.id;
                          const isWinner = winnerIds.has(nominee.id);
                          const isCorrect = hasWinner && isSelected && isWinner;
                          const isIncorrect = hasWinner && isSelected && !isWinner;

                          return (
                            <label
                              key={nominee.id}
                              className={`nominee-item ${isSelected ? 'selected' : ''} ${isWinner ? 'winner' : ''} ${isCorrect ? 'correct' : ''} ${isIncorrect ? 'incorrect' : ''} ${submissionLocked ? 'locked' : ''}`}
                            >
                              <input
                                type="radio"
                                name={`category-${category.id}`}
                                value={nominee.id}
                                checked={isSelected}
                                onChange={() => handlePredictionChange(category.id, nominee.id)}
                                disabled={submissionLocked}
                                className="nominee-radio"
                              />
                              <span className="nominee-letter">{nominee.code}.</span>
                              <span className="nominee-name">
                                {nominee.name}
                                {isCorrect && <span className="winner-badge">🏆 Winner</span>}
                                {isWinner && !isCorrect && <span className="winner-badge">🏆 Winner</span>}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <p className="no-nominees">No nominees available</p>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Bonus Questions */}
              {bonusQuestions.map((question) => {

                const response = bonusQuestionResponses[question.id] || '';
                const winners = Array.isArray(question.winners) ? question.winners : [];
                const bonusResolved = winners.length > 0;
                const bonusCorrect = bonusResolved && winners.some((winner) => winner.userId === userId);
                const bonusIncorrect = bonusResolved && !bonusCorrect && Boolean(response.trim());
                const winningResponse = winners[0]?.guessedResponse || question.answer || '';

                return (
                  <div
                    key={question.id}
                    className={`ballot-category bonus-question ${bonusCorrect ? 'bonus-question--correct' : ''} ${bonusIncorrect ? 'bonus-question--incorrect' : ''}`.trim()}
                  >
                    <div className="bonus-question-header">
                      <strong className="bonus-question-title">Bonus Question</strong>
                    </div>
                    <div className="bonus-question-content">
                      <div className="bonus-question-text">{question.question}</div>
                      <input
                        type="text"
                        className={`bonus-question-input ${bonusCorrect ? 'bonus-question-input--correct' : ''} ${bonusIncorrect ? 'bonus-question-input--incorrect' : ''}`.trim()}
                        value={response}
                        onChange={(e) => handleBonusQuestionChange(question.id, e.target.value)}
                        placeholder="Enter your answer..."
                        disabled={submissionLocked}
                      />
                      {bonusResolved && winningResponse && (
                        <div className="bonus-question-answer">
                          <span className="bonus-question-answer__label">Answer:</span>{' '}
                          <span className="bonus-question-answer__value">{winningResponse}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!hideSubmissionDeadlineUi && (
              <div className="form-actions">
                {submissionLocked ? (
                  <div className="submission-locked-message">
                    <span className="lock-icon">🔒</span>
                    <span>Submissions are closed</span>
                  </div>
                ) : (
                  <div className="ballot-submit__btn-wrap">
                    <button
                      type="submit"
                      className="ballot-submit__btn"
                      disabled={submitting}
                    >
                      {submitting
                        ? 'Submitting...'
                        : 'Submit Ballot'
                      }
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default BallotSubmissionPage;
